import { GoogleGenAI } from "@google/genai";
import { z } from "zod";
import { zodToJsonSchema } from "zod-to-json-schema";

// Constants for AI configuration
const AI_CONFIG = {
    MODEL_NAME: "gemini-2.5-flash",
    IMAGE_MODEL_NAME: "gemini-2.5-flash-image",
    API_KEY: import.meta.env.VITE_GEMINI_API_KEY
};

// Lazy-initialize to avoid crashing the app when API key is not set
let _ai = null;
const getAI = () => {
    if (!_ai) {
        if (!AI_CONFIG.API_KEY) {
            throw new Error('Gemini API key is not configured. Set VITE_GEMINI_API_KEY.');
        }
        _ai = new GoogleGenAI({ apiKey: AI_CONFIG.API_KEY });
    }
    return _ai;
};

// --- SCHEMAS ---

const StrategicAdviceSchema = z.object({
    analysis: z.string().describe("Detailed analysis of the businesses found via search."),
    sales_pitch: z.string().describe("A personalized sales pitch for the business."),
    technical_roadmap: z.string().describe("Technical support and onboarding steps."),
    market_trends: z.array(z.string()).describe("List of relevant market trends or competitor notes.")
});

const MenuSchema = z.object({
    menuName: z.string().describe("Professional title for the menu"),
    description: z.string().describe("Short branding description"),
    categories: z.array(z.object({
        categoryName: z.string().describe("Category name (e.g. Appetizers, Main Course, Drinks)"),
        items: z.array(z.object({
            name: z.string().describe("Item Name"),
            price: z.string().describe("Price as a string (e.g. $12.50 or 150 TL)"),
            description: z.string().describe("Yummy sounding description"),
            image: z.string().optional().describe("Leave empty or ignore")
        }))
    })),
    designRecommendation: z.object({
        primaryColor: z.string().describe("A hex code that matches the vibe"),
        fontStyle: z.string().describe("serif or sans-serif")
    })
});

// --- PROMPTS ---

const SYSTEM_PROMPTS = {
    MENU_DESIGNER: `You are an expert restaurant consultant and menu designer. 
    Your task is to create a complete, professional menu based on the user's description.
    Respond ONLY with a valid JSON object.`,
    
    LOYALTY_SUPPORT: (contextData) => `Seni "Premium CRM Teknik Destek ve Uygulama Rehberi" olarak tanımlıyorum.
    Görevin: İşletme yöneticisine uygulamayı nasıl kullanacağını, hangi özelliklerin nerede olduğunu ve işlem adımlarını anlatmaktır.
    
    KURALLAR:
    1. SADECE uygulamadaki mevcut özellikleri ve menüleri anlat (Müşteriler, Bildirimler, Dijital Menü, Ayarlar, Kampanyalar, Ödüller, QR Kod).
    2. Stratejik tavsiye, pazarlama fikri veya "yeni fikirler" ASLA verme. Sadece teknik olarak "nasıl yapılır" sorusuna odaklan.
    3. Cevaplarını mutlaka adım adım (1. ..., 2. ...) şeklinde yapılandır.
    4. İşletme verilerini analiz ederek konuş:
       - İşletme Adı: ${contextData?.businessName || 'N/A'}
       - Toplam Müşteri: ${contextData?.totalCustomers || 0}
       - Aktif Kampanyalar: ${JSON.stringify(contextData?.activeDiscounts || [])}
       - Aktif Ödüller: ${JSON.stringify(contextData?.availableRewards || [])}
    
    Respond in user's language.`,

    SUPER_ADMIN_CONSULTANT: (contextData) => `You are the "Senior Platform Growth Consultant" for Premium CRM.
    Your mission is to help the Super Admin expand.

    Platform Context:
    - Registered Businesses: ${JSON.stringify(contextData?.tenants || [])}
    - Recent Meetings: ${JSON.stringify(contextData?.meetings || [])}

    CORE CAPABILITY: GOOGLE SEARCH. Research every lead and trend thoroughly.
    Respond in user's language (${contextData?.lang || 'Turkish'}).`
};

// --- HELPER FUNCTIONS ---

const cleanAIJsonResponse = (text) => {
    if (!text) return null;
    return text.replace(/```json|```/g, "").trim();
};

const mapChatHistory = (history) => {
    return history
        .filter(msg => msg.role !== 'assistant' || history.indexOf(msg) > 0)
        .map(msg => ({
            role: msg.role === 'assistant' ? 'model' : 'user',
            parts: [{ text: msg.content }]
        }));
};

// --- EXPORTED SERVICES ---

/**
 * Generates an image using Gemini multimodality.
 */
export const generateImageWithGemini = async (prompt) => {
    try {
        const response = await getAI().models.generateContent({
            model: AI_CONFIG.IMAGE_MODEL_NAME,
            contents: [{
                role: "user",
                parts: [{ text: `Generate a high-quality, professional food photograph of: ${prompt}. Studio lighting, detailed texture, 4k resolution.` }]
            }],
        });

        const imagePart = response.candidates?.[0]?.content?.parts?.find(p => p.inlineData || p.fileData);
        if (imagePart?.inlineData) {
            return `data:${imagePart.inlineData.mimeType};base64,${imagePart.inlineData.data}`;
        }
        throw new Error("IMAGE_GENERATION_FAILED");
    } catch (error) {
        console.error("Gemini Image Error:", error);
        throw error;
    }
};

/**
 * Generates a professional menu JSON.
 */
export const generateMenu = async (prompt) => {
    try {
        const response = await getAI().models.generateContent({
            model: AI_CONFIG.MODEL_NAME,
            contents: [{ role: "user", parts: [{ text: SYSTEM_PROMPTS.MENU_DESIGNER + "\n\n" + prompt }] }],
            config: {
                responseMimeType: "application/json",
                responseJsonSchema: zodToJsonSchema(MenuSchema)
            }
        });

        const text = response.text || response.candidates?.[0]?.content?.parts?.[0]?.text;
        const cleanJson = cleanAIJsonResponse(text);
        if (!cleanJson) throw new Error("EMPTY_AI_RESPONSE");
        
        return JSON.parse(cleanJson);
    } catch (error) {
        console.error("Gemini Menu Error:", error);
        throw new Error("Failed to generate menu: " + error.message);
    }
};

/**
 * Technical support chat for Business Owners.
 */
export const chatWithLoyaltyAI = async (userInput, history, contextData) => {
    try {
        const contents = mapChatHistory(history);
        contents.push({ role: 'user', parts: [{ text: userInput }] });

        const response = await getAI().models.generateContent({
            model: AI_CONFIG.MODEL_NAME,
            contents: contents,
            config: {
                systemInstruction: SYSTEM_PROMPTS.LOYALTY_SUPPORT(contextData)
            }
        });

        return response.text;
    } catch (error) {
        console.error("Loyalty AI Error:", error);
        throw new Error("Failed to get response from AI.");
    }
};

/**
 * Strategic research and growth chat for Super Admins.
 */
export const chatWithSuperAdminAI = async (userInput, history, contextData) => {
    try {
        const contents = mapChatHistory(history);
        contents.push({ role: 'user', parts: [{ text: userInput }] });

        const response = await getAI().models.generateContent({
            model: AI_CONFIG.MODEL_NAME,
            contents: contents,
            config: {
                systemInstruction: SYSTEM_PROMPTS.SUPER_ADMIN_CONSULTANT(contextData),
                tools: [{ googleSearch: {} }],
                responseMimeType: "application/json",
                responseJsonSchema: zodToJsonSchema(StrategicAdviceSchema),
            },
        });

        const cleanJson = cleanAIJsonResponse(response.text);
        if (!cleanJson) throw new Error("EMPTY_AI_RESPONSE");

        const parsedData = StrategicAdviceSchema.parse(JSON.parse(cleanJson));

        return `### 📊 Stratejik Analiz\n${parsedData.analysis}\n\n` +
            `### 🎯 Satış Teklifi (Sales Pitch)\n${parsedData.sales_pitch}\n\n` +
            `### 🛠️ Teknik Yol Haritası\n${parsedData.technical_roadmap}\n\n` +
            `### 📈 Pazar Trendleri\n${parsedData.market_trends.map(t => `- ${t}`).join('\n')}`;

    } catch (error) {
        console.error("Super Admin AI Error:", error);
        if (error instanceof z.ZodError || error instanceof SyntaxError) {
            return "Üzgünüm, analiz sonuçlarını yapılandırırken bir hata oluştu.";
        }
        return "Şu anda analiz yapılamıyor: " + error.message;
    }
};
